import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TagInput } from "@/components/TagInput";
import { ArrowLeft, ArrowRight, Save, CheckCircle2, Sparkles } from "lucide-react";

const STEPS = ["Offre proposée", "Ciblage", "Signaux commerciaux", "Exclusions", "Paramètres d'envoi"];

const SIGNAUX = [
  { id: "recrutement_en_cours", label: "L'entreprise recrute (surcharge → besoin possible de vos services)" },
  { id: "croissance", label: "Croissance (nouveaux besoins à couvrir)" },
  { id: "ouverture_etablissement", label: "Ouverture d'un établissement (nouveaux besoins)" },
  { id: "evenement_annonce", label: "Événement annoncé (prestation à prévoir)" },
  { id: "salon_professionnel", label: "Participe à un salon professionnel" },
  { id: "besoin_saisonnier", label: "Pic d'activité saisonnier" },
  { id: "remplacement_personnel", label: "Doit pallier une absence (opportunité de prestation)" },
  { id: "activite_importante", label: "Forte activité (surcharge possible)" },
];

const EXCLUSIONS = [
  { id: "exclure_clients_existants", label: "Clients existants" },
  { id: "exclure_deja_contactes", label: "Prospects déjà contactés" },
  { id: "exclure_concurrents", label: "Concurrents" },
  { id: "exclure_desinscrits", label: "Entreprises désinscrites" },
  { id: "exclure_exclusions_manuelles", label: "Entreprises exclues manuellement" },
];

const JOURS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];

const EMPTY = {
  nom: "", service_vendu: "", description_offre: "", angle_commercial: "", site_internet: "", benefices_clients: [], preuves_references: [], objectif: "",
  secteurs: [], sous_secteurs: [], codes_naf: [], villes: [], departements: [], regions: [],
  rayon_km: "", taille_min: "", taille_max: "", nb_etablissements_min: "", fonctions_decideurs: [],
  signaux: [], autre_signal: "",
  exclure_clients_existants: true, exclure_deja_contactes: true, exclure_concurrents: true,
  exclure_desinscrits: true, exclure_exclusions_manuelles: true, secteurs_exclus: [], domaines_exclus: [],
  volume_quotidien_max: 20, heure_debut: "09:00", heure_fin: "18:00",
  jours_autorises: ["lundi", "mardi", "mercredi", "jeudi", "vendredi"],
  nb_relances_max: 2, delai_relances_jours: 5, style_message: "direct",
  adresse_expedition: "", commercial_responsable_id: null,
};

const Field = ({ label, children, hint }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export default function CampaignWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [c, setC] = useState(EMPTY);
  const [campaignId, setCampaignId] = useState(id || null);
  const [statut, setStatut] = useState("brouillon");
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [strat, setStrat] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const analyzeStrategy = async () => {
    if (!c.service_vendu.trim()) {
      toast.error("Renseignez d'abord le service vendu (étape 1)");
      return;
    }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/strategy/analyze", {
        service: c.service_vendu,
        description: c.description_offre,
        zone: [...c.villes, ...c.regions].join(", "),
      });
      setStrat(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const applyStrategy = () => {
    const merge = (a, b) => [...new Set([...(a || []), ...(b || [])])];
    setC({
      ...c,
      secteurs: merge(c.secteurs, strat.secteurs),
      sous_secteurs: merge(c.sous_secteurs, (strat.cibles || []).map((t) => t.type)),
      codes_naf: merge(c.codes_naf, (strat.codes_naf || []).map((n) => n.code)),
      fonctions_decideurs: merge(c.fonctions_decideurs, strat.decideurs),
    });
    setStrat(null);
    toast.success("Ciblage appliqué — vérifiez et ajustez les champs");
  };

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    if (id) {
      api.get(`/campaigns/${id}`).then(({ data }) => {
        const { id: _, company_id, statut: st, created_by, created_at, updated_at, validated_at, validated_by, ...rest } = data;
        setC({ ...EMPTY, ...Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, v ?? EMPTY[k] ?? ""])) });
        setStatut(st);
      }).catch((e) => toast.error(formatApiError(e)));
    }
  }, [id]);

  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const setV = (k) => (v) => setC({ ...c, [k]: v });
  const setNum = (k) => (e) => setC({ ...c, [k]: e.target.value });

  const buildPayload = () => {
    const num = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v, 10));
    return {
      ...c,
      rayon_km: num(c.rayon_km), taille_min: num(c.taille_min), taille_max: num(c.taille_max),
      nb_etablissements_min: num(c.nb_etablissements_min),
      volume_quotidien_max: num(c.volume_quotidien_max) ?? 20,
      nb_relances_max: num(c.nb_relances_max) ?? 2,
      delai_relances_jours: num(c.delai_relances_jours) ?? 5,
    };
  };

  const save = async (silent = false) => {
    if (!c.nom.trim()) {
      toast.error("Le nom de la campagne est obligatoire.");
      return null;
    }
    setSaving(true);
    try {
      let data;
      if (campaignId) {
        ({ data } = await api.put(`/campaigns/${campaignId}`, buildPayload()));
      } else {
        ({ data } = await api.post("/campaigns", buildPayload()));
        setCampaignId(data.id);
      }
      if (!silent) toast.success("Brouillon enregistré");
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const validate = async () => {
    const saved = await save(true);
    if (!saved) return;
    try {
      await api.post(`/campaigns/${saved.id}/validate`);
      toast.success("Campagne validée !");
      navigate("/campagnes");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleIn = (k, v) => setC({ ...c, [k]: c[k].includes(v) ? c[k].filter((x) => x !== v) : [...c[k], v] });

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="campaign-wizard">
      <div>
        <button onClick={() => navigate("/campagnes")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="wizard-back-link">
          <ArrowLeft size={14} /> Retour aux campagnes
        </button>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="font-display text-3xl font-semibold">{id ? "Modifier la campagne" : "Nouvelle campagne"}</h1>
          <Badge variant={statut === "validee" ? "default" : "secondary"} className={statut === "validee" ? "bg-accent" : ""}>
            {statut === "validee" ? "Validée" : "Brouillon"}
          </Badge>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="wizard-steps">
        {STEPS.map((s, i) => (
          <button
            key={s}
            onClick={() => setStep(i)}
            data-testid={`wizard-step-${i + 1}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
              i === step ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
            }`}
          >
            {i + 1}. {s}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-5 p-6">
          {step === 0 && (
            <>
              <Field label="Nom de la campagne *"><Input value={c.nom} onChange={set("nom")} data-testid="campaign-name-input" /></Field>
              <Field label="Service vendu *"><Input value={c.service_vendu} onChange={set("service_vendu")} data-testid="campaign-service-input" /></Field>
              <Field label="Description de l'offre"><Textarea rows={3} value={c.description_offre} onChange={set("description_offre")} data-testid="campaign-description-input" /></Field>
              <Field label="Angle commercial du message" hint="Comment aborder le prospect. Ex : « Message court : demander si l'entreprise a besoin de personnel et l'inviter à découvrir renforteo.fr »">
                <Textarea rows={2} value={c.angle_commercial} onChange={set("angle_commercial")} data-testid="campaign-angle-input" />
              </Field>
              <Field label="Site internet à mentionner dans les e-mails" hint="Utilisé comme appel à l'action. Ex : https://www.renforteo.fr">
                <Input value={c.site_internet} onChange={set("site_internet")} data-testid="campaign-website-input" />
              </Field>
              <Field label="Bénéfices clients"><TagInput value={c.benefices_clients} onChange={setV("benefices_clients")} testId="campaign-benefits-input" /></Field>
              <Field label="Preuves et références"><TagInput value={c.preuves_references} onChange={setV("preuves_references")} testId="campaign-proofs-input" /></Field>
              <Field label="Objectif de la campagne"><Textarea rows={2} value={c.objectif} onChange={set("objectif")} data-testid="campaign-goal-input" /></Field>
            </>
          )}

          {step === 1 && (
            <>
              <div className="rounded-md border border-accent/40 bg-accent/5 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm"><span className="font-medium">Stratège commercial IA</span> — propose automatiquement les cibles, codes NAF et décideurs adaptés à votre service.</p>
                  <Button size="sm" variant="outline" onClick={analyzeStrategy} disabled={analyzing} data-testid="strategy-analyze-button">
                    <Sparkles size={14} className="mr-1.5" /> {analyzing ? "Analyse IA…" : "Suggérer les cibles"}
                  </Button>
                </div>
                {strat && (
                  <div className="mt-3 space-y-2 border-t pt-3 text-sm" data-testid="strategy-results">
                    <p className="text-xs text-amber-700">{strat.avertissement}</p>
                    <p><span className="font-medium">Cibles proposées :</span> {(strat.cibles || []).map((t) => t.type).join(" · ")}</p>
                    <p><span className="font-medium">Codes NAF :</span> {(strat.codes_naf || []).map((n) => `${n.code} (${n.libelle})`).join(" · ")}</p>
                    <p><span className="font-medium">Décideurs :</span> {(strat.decideurs || []).join(" · ")}</p>
                    <p><span className="font-medium">Fréquence du besoin :</span> {strat.frequence_besoin} · <span className="font-medium">Valeur client :</span> {strat.valeur_client}</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={applyStrategy} data-testid="strategy-apply-button">Appliquer au ciblage</Button>
                      <Button size="sm" variant="ghost" onClick={() => setStrat(null)}>Ignorer</Button>
                    </div>
                  </div>
                )}
              </div>
              <Field label="Secteurs d'activité ciblés *"><TagInput value={c.secteurs} onChange={setV("secteurs")} placeholder="Ex : Hôtellerie" testId="campaign-sectors-input" /></Field>
              <Field label="Sous-secteurs"><TagInput value={c.sous_secteurs} onChange={setV("sous_secteurs")} testId="campaign-subsectors-input" /></Field>
              <Field label="Codes NAF (optionnel)"><TagInput value={c.codes_naf} onChange={setV("codes_naf")} placeholder="Ex : 5629A" testId="campaign-naf-input" /></Field>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Villes"><TagInput value={c.villes} onChange={setV("villes")} testId="campaign-cities-input" /></Field>
                <Field label="Départements"><TagInput value={c.departements} onChange={setV("departements")} placeholder="Ex : 69" testId="campaign-departments-input" /></Field>
                <Field label="Régions"><TagInput value={c.regions} onChange={setV("regions")} testId="campaign-regions-input" /></Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-4">
                <Field label="Rayon (km)"><Input type="number" min="0" value={c.rayon_km} onChange={setNum("rayon_km")} data-testid="campaign-radius-input" /></Field>
                <Field label="Taille min. (salariés)"><Input type="number" min="0" value={c.taille_min} onChange={setNum("taille_min")} data-testid="campaign-sizemin-input" /></Field>
                <Field label="Taille max."><Input type="number" min="0" value={c.taille_max} onChange={setNum("taille_max")} data-testid="campaign-sizemax-input" /></Field>
                <Field label="Nb établissements min."><Input type="number" min="0" value={c.nb_etablissements_min} onChange={setNum("nb_etablissements_min")} data-testid="campaign-establishments-input" /></Field>
              </div>
              <Field label="Fonctions décisionnaires recherchées *" hint="Ex : dirigeant, directeur commercial, responsable RH, responsable événementiel, responsable achats, directeur d'établissement, responsable exploitation…">
                <TagInput value={c.fonctions_decideurs} onChange={setV("fonctions_decideurs")} testId="campaign-decisionmakers-input" />
              </Field>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-muted-foreground">
                Signaux qui indiquent qu'une entreprise a probablement besoin de <strong>vos prestations</strong> (clients potentiels à prioriser).
                Exemple : une entreprise qui recrute sans succès est une cliente idéale pour une offre de renfort ou de sous-traitance.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {SIGNAUX.map((s) => (
                  <label key={s.id} className="flex cursor-pointer items-center gap-2.5 rounded-md border p-3 text-sm hover:bg-secondary/50">
                    <Checkbox checked={c.signaux.includes(s.id)} onCheckedChange={() => toggleIn("signaux", s.id)} data-testid={`signal-${s.id}`} />
                    {s.label}
                  </label>
                ))}
              </div>
              <Field label="Autre signal défini par vous"><Input value={c.autre_signal} onChange={set("autre_signal")} data-testid="campaign-customsignal-input" /></Field>
            </>
          )}

          {step === 3 && (
            <>
              <p className="text-sm text-muted-foreground">Catégories exclues automatiquement avant tout contact.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {EXCLUSIONS.map((ex) => (
                  <label key={ex.id} className="flex cursor-pointer items-center gap-2.5 rounded-md border p-3 text-sm hover:bg-secondary/50">
                    <Checkbox checked={!!c[ex.id]} onCheckedChange={(v) => setC({ ...c, [ex.id]: !!v })} data-testid={`exclusion-${ex.id}`} />
                    {ex.label}
                  </label>
                ))}
              </div>
              <Field label="Secteurs non souhaités"><TagInput value={c.secteurs_exclus} onChange={setV("secteurs_exclus")} testId="campaign-excludedsectors-input" /></Field>
              <Field label="Domaines internet exclus"><TagInput value={c.domaines_exclus} onChange={setV("domaines_exclus")} placeholder="Ex : concurrent.fr" testId="campaign-excludeddomains-input" /></Field>
            </>
          )}

          {step === 4 && (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Volume max. quotidien"><Input type="number" min="1" value={c.volume_quotidien_max} onChange={setNum("volume_quotidien_max")} data-testid="campaign-volume-input" /></Field>
                <Field label="Heure de début"><Input type="time" value={c.heure_debut} onChange={set("heure_debut")} data-testid="campaign-starttime-input" /></Field>
                <Field label="Heure de fin"><Input type="time" value={c.heure_fin} onChange={set("heure_fin")} data-testid="campaign-endtime-input" /></Field>
              </div>
              <Field label="Jours autorisés">
                <div className="flex flex-wrap gap-2">
                  {JOURS.map((j) => (
                    <button
                      key={j}
                      type="button"
                      onClick={() => toggleIn("jours_autorises", j)}
                      data-testid={`day-${j}`}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                        c.jours_autorises.includes(j) ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      {j}
                    </button>
                  ))}
                </div>
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Nombre max. de relances"><Input type="number" min="0" max="5" value={c.nb_relances_max} onChange={setNum("nb_relances_max")} data-testid="campaign-followups-input" /></Field>
                <Field label="Délai entre relances (jours)"><Input type="number" min="1" value={c.delai_relances_jours} onChange={setNum("delai_relances_jours")} data-testid="campaign-delay-input" /></Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Style du message">
                  <Select value={c.style_message} onValueChange={setV("style_message")}>
                    <SelectTrigger data-testid="campaign-style-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct">Direct et commercial</SelectItem>
                      <SelectItem value="premium">Premium et institutionnel</SelectItem>
                      <SelectItem value="chaleureux">Chaleureux et local</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Commercial responsable">
                  <Select value={c.commercial_responsable_id || "none"} onValueChange={(v) => setC({ ...c, commercial_responsable_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="campaign-sales-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non attribué</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Adresse d'expédition"><Input value={c.adresse_expedition} onChange={set("adresse_expedition")} placeholder="contact@…" data-testid="campaign-sender-input" /></Field>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 pb-8">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-prev-button">
          <ArrowLeft size={15} className="mr-1.5" /> Précédent
        </Button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => save()} disabled={saving} data-testid="wizard-save-draft-button">
            <Save size={15} className="mr-1.5" /> Enregistrer le brouillon
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} data-testid="wizard-next-button">
              Suivant <ArrowRight size={15} className="ml-1.5" />
            </Button>
          ) : (
            <Button onClick={validate} disabled={saving} className="bg-accent hover:bg-accent/90" data-testid="wizard-validate-button">
              <CheckCircle2 size={15} className="mr-1.5" /> Valider la campagne
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
